import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getMyCV } from "@service/cv";
import { applyForJob, getJobDetail, setApplicationStatus } from "@service/job";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import ResumeUpload from "@page/apply-now-resume-upload";
import Quiz from "@page/apply-now-quiz";
import WorkAuthorization from "@page/apply-now-work-authorization";
import Complete from "@page/apply-now-complete";
import { JobApplication, Job } from "@type/job";
import { CVDataInfo } from "@type/cv";
import { getAssessment, getUserInterviewSubmission } from "@service/assessment";
import { FormAssessment, InterviewAssessment } from "@type/assessments";
import AiInterview from "@page/ai-interview";
import { getInterviewStatus, getVideo } from "@service/interview";
import CustomVoiceInterview from "@container/custom-voice-interview";
import { CustomInterviewSection } from "@type/videoInterview";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { DailyCall } from "@daily-co/daily-js";
import { useUserProfileContext } from "@context/user_profile";
import { formatCV, inArrayString } from "@util/string";
import { getCalculateMultilevelSimilarityScore } from "@service/employer";
import ApplyRejectionCard from "@container/apply-rejection-card";
import { interviewStatusComplete, stageApplyInfo } from "@constant/app";
import { catchErrorLog } from "@util/sentry";
import ApplicationRejection from "@container/application-rejection";
import { Company } from "@type/company";
import { getCompanyInfo } from "@service/user";
import { authSendWelcomEmail } from "@service/auth";
import SkipInterviewDialog from "@container/dialog/dialog-skip-interview";
import { trackEvent } from "@util/mixpanel";
import ApplicationDraft from "@container/applicantion-draft";
import { ChevronLeftIcon } from "lucide-react";
import { cn } from "@util/tailwind";
import { SnackbarProvider } from "@hook/use-snackbar";

const MIN_SCORE_CCBV_RESUME = 10;
const MIN_SCORE_CCBV_ASSESSMENT = 10;
const MIN_SCORE_CCBV_AI_INTERVIEW = 10;

export default function ApplyNow() {
  const [isLoadingUserProfile, userInfo] = useUserProfileContext();
  const { jobid } = useParams();
  const [isLoadingSendWelcomeEmail, setLoadingSendWelcomeEmail] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [failedOnStep, setFailedOnStep] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [jobDetail, setJobDetail] = useState<Job | undefined>(undefined);
  const [isLoadingJobDetail, setIsLoadingJobDetail] = useState(true);
  const [cvData, setCvData] = useState<CVDataInfo | null>(null);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [isLoadingScore, setLoadingScore] = useState<boolean>(false);
  const [isLoadingCV, setLoadingCV] = useState<boolean>(false);
  const [isLoadingContinue, setLoadingContinue] = useState<boolean>(false);
  const [score, setScore] = useState<number>(0);
  const [interviewCompleted, setInterviewCompleted] = useState<boolean>(false);
  const [isLoadingAssessment, setIsLoadingAssessment] = useState<boolean>(false);
  const [isLoadingAIInterview, setIsLoadingAIInterview] = useState<boolean>(false);
  const [isLoadingCompanyInfo, setIsLoadingCompanyInfo] = useState<boolean>(true);
  const [isSkipDialogOpen, setIsSkipDialogOpen] = useState<boolean>(false);
  const [isSkippedAIInterview, setIsSkippedAIInterview] = useState<boolean>(false);
  const [hideBackButton, setHideBackButton] = useState<boolean>(false);

  const [assessment, setAssessment] = useState<FormAssessment | null>(null);
  const [interviewAssessment, setInterviewAssessment] = useState<InterviewAssessment | null>(null);
  const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
  const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);
  const [jobApplication, setJobApplication] = useState<
    JobApplication | undefined
  >(undefined);
  const [currentTipIndex, setCurrentTipIndex] = useState<number>(0);

  const callObject = useCallObject({}) as DailyCall;

  const tips: string[] = [
    "Almost there — getting your insights ready!",
    "Listening closely... even to the pauses.",
    "Analyzing your answers frame by frame.",
    "Spotting strengths and growth areas.",
    "Running the final checks on your delivery."
  ];

  useEffect(() => {
    if ((currentStep === 2 || currentStep === 3) && isLoadingScore) {
      // Start a timer to change the tip every 5 seconds
      let interval: NodeJS.Timeout | undefined;
      if (currentTipIndex < tips.length - 1) {
        interval = setInterval(() => {
          setCurrentTipIndex((prevIndex) => {
            if (prevIndex < tips.length - 1) {
              return prevIndex + 1;
            } else {
              return 0;
            }
          });
        }, 3000);
      }

      return () => clearInterval(interval);
    }
  }, [interviewAssessment, isLoadingScore, currentStep]);

  const fetchCV = async () => {
    try {
      setLoadingCV(true);
      const response = await getMyCV();
      if (response.data) {
        // Add verification that data is not empty
        const hasValidData =
          response.data &&
          typeof response.data === "object" &&
          Object.keys(response.data).length > 0;

        if (hasValidData) {
          setCvData(response.data);
        } else {
          setCvData(null);
        }
      } else {
        setCvData(null);
      }
    } catch (error) {
      setCvData(null);
      catchErrorLog(`Error fetching CV: ${error}`);
    } finally {
      setLoadingCV(false);
    }
  };

  const fetchJobDetail = async () => {
    if (!jobid) {
      setError("No job ID provided");
      setIsLoadingJobDetail(false);
      return;
    }

    try {
      setIsLoadingJobDetail(true);
      setIsLoadingAIInterview(true);
      const data = await getJobDetail(jobid);
      setJobDetail(data);
      if (data.assessment_ids && data.assessment_ids.length > 0) {
        // Process all assessments
        setIsLoadingAssessment(true);
        for (const assessmentId of data.assessment_ids) {
          const assessment_data = await getAssessment(assessmentId.id);
          if (assessment_data) {
            if (assessment_data.type === "Form") {
              setAssessment(assessment_data as FormAssessment);
            } else if (assessment_data.type === "Interview") {
              const interviewData = assessment_data as InterviewAssessment;
              setInterviewAssessment(interviewData);

              // Format sections for the CustomVoiceInterview component
              const sections = interviewData.content.sections.map(section => ({
                title: section.title,
                instruction: section.instruction,
                duration: section.duration,
                questions: (section.questions || []),
                transitions: section.transition ? { before: section.transition } : undefined
              }));
              setFormattedSections(sections);
            }
          }
        }
        setIsLoadingAssessment(false);
        setIsLoadingAIInterview(false);
      } else {
        setIsLoadingAIInterview(false);
        getInterviewStatus(null).then((res) => {
          if (res?.data?.status === "Complete") {
            setInterviewCompleted(true);
            setCurrentStep(3);
          }
        })
      }

      const respCompany = await getCompanyInfo(data.company_id);
      if (respCompany.status === 200) {
        setCompanyInfo(respCompany.data.data);
      }
      setIsLoadingCompanyInfo(false);
    } catch (err) {
      console.log("Error fetching job details:", err);
      setError("Failed to fetch job details");
      catchErrorLog(`Error fetching job details: ${err}`);
    } finally {
      setIsLoadingJobDetail(false);
    }
  };

  useEffect(() => {
    fetchCV();
    fetchJobDetail();
    // getInterviewStatus(null).then((res) => {
    //   if (res?.data?.status === "Complete") {
    //     setInterviewCompleted(true);
    //     setCurrentStep(3);
    //   }
    // })
  }, []);

  useEffect(() => {
    if (!isLoadingAssessment && interviewAssessment) {
      getUserInterviewSubmission(interviewAssessment.id || "", userInfo?.id || "").then((response) => {
        if (response?.conversation_id && response?.status === interviewStatusComplete) {
          setInterviewCompleted(true);
        } else {
          setInterviewCompleted(false);
        }
      }).finally(() => {
        setIsLoadingAIInterview(false);
      });
    }
  }, [isLoadingAssessment, interviewAssessment])

  useEffect(() => {
    if (userInfo && !!jobDetail && inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
      // update resume
      if (currentStep === 1) {
        setLoadingScore(true);
        getCalculateMultilevelSimilarityScore({
          job_id: jobDetail.id,
        }).then((resp) => {
          if (resp.status === 200) {
            setScore(resp.data.data.similarity_score)
            if (resp.data.data.similarity_score <= MIN_SCORE_CCBV_RESUME) {
              setFailedOnStep(1);
            } else {
              setCurrentStep(2);
            }
          }
        })
          .catch((error) => {
            catchErrorLog(`Error fetching similarity score: ${error}`);
            setError("Failed to calculate similarity score");
          })
          .finally(() => {
            setLoadingScore(false);
          });
      }
    }
  }, [jobDetail, currentStep, userInfo]);

  const handleCheckAssessmentScore = () => {
    if (!inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
      handleNextStep();
      return;
    }

    setLoadingScore(true);
    getCalculateMultilevelSimilarityScore({
      job_id: jobDetail?.id || "",
      assessment_id: assessment?.id || "",
    }).then((resp) => {
      if (resp.status === 200) {
        setScore(resp.data.data.similarity_score)
        if (resp.data.data.similarity_score <= MIN_SCORE_CCBV_ASSESSMENT) {
          setFailedOnStep(2);
        } else {
          handleNextStep();
        }
      }
    })
      .catch((error) => {
        catchErrorLog(`Error fetching similarity score: ${error}`);
        setError("Failed to calculate similarity score");
      })
      .finally(() => {
        setLoadingScore(false);
      });
  }

  const handleCheckInterviewScore = async () => {
    trackEvent('ai_interview_completed', {
      job_id: jobDetail?.id || "",
    })

    if (!inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
      handleNextStep();
      return;
    }

    setLoadingScore(true);
    if (!interviewAssessment) {
      getVideo(undefined, "First Time Interview", userInfo?.id)
        .then((resp) => {
          if (resp) {
            return getCalculateMultilevelSimilarityScore({
              job_id: jobDetail?.id || "",
              conversation_id: resp.conversation_id,
            }).then((resp) => {
              if (resp.status === 200) {
                setScore(resp.data.data.similarity_score)
                if (resp.data.data.similarity_score <= MIN_SCORE_CCBV_AI_INTERVIEW) {
                  setFailedOnStep(3);
                } else {
                  handleNextStep();
                }
              }
            })
          }
        }).finally(() => {
          setLoadingScore(false);
        });
      return;
    }

    const submission = await getUserInterviewSubmission(interviewAssessment?.id || "", userInfo?.id || "")
    if (submission && submission.conversation_id) {
      setTimeout(() => {
        let retryCount = 0;
        const maxRetries = 3;
        const baseDelay = 3000;

        const attemptScoreCalculation = () => {
          getCalculateMultilevelSimilarityScore({
            job_id: jobDetail?.id || "",
            conversation_id: submission.conversation_id,
          }).then((resp) => {
            if (resp.status === 200) {
              setScore(resp.data.data.similarity_score)
              if (resp.data.data.similarity_score <= MIN_SCORE_CCBV_AI_INTERVIEW) {
                setFailedOnStep(3);
              } else {
                handleNextStep();
              }
            }
          }).catch((error) => {
            catchErrorLog(`Error fetching similarity score (attempt ${retryCount + 1}): ${error}`);

            if (retryCount < maxRetries) {
              retryCount++;
              const delay = baseDelay * Math.pow(2, retryCount - 1); // Exponential backoff
              console.log(`Retrying in ${delay}ms... (${retryCount}/${maxRetries})`);
              setTimeout(attemptScoreCalculation, delay);
            } else {
              setError("Failed to calculate similarity score after multiple attempts");
            }
          }).finally(() => {
            setLoadingScore(false);
          })
        };

        attemptScoreCalculation();
      }, 30000); // Wait for 30 seconds before checking the score
    }
  }

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 5));
  };


  const handlePrevStep = () => {
    if (currentStep === 3 && !assessment) {
      setCurrentStep(1); // If no assessment and at interview step, go back to resume upload
    } else {
      setCurrentStep((prev) => Math.max(prev - 1, 1));
    }
  };

  // This function will be passed to ResmeUpload and called when a CV is uploaded successfully
  const handleUploadSuccess = (data: CVDataInfo) => {
    if (data && typeof data === "object") {
      setCvData(data);
    } else {
      setError("Received invalid data in upload success handler");
      catchErrorLog(`Received invalid data in upload success handler: ${JSON.stringify(data)}`);
    }
  };

  const handleSkipInterivew = () => {
    trackEvent('job_saved_as_draft');
    setLoadingSendWelcomeEmail(true);
    setIsSkippedAIInterview(true);
    applyForJob({
      job_id: jobDetail?.id || "",
      is_draft: true,
      drop_at_stage: 2,
    }).then(async (resp) => {
      if (resp) {
        return authSendWelcomEmail({
          finished_interview: false,
          job_id: jobDetail?.id || "",
          eventTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          eventTimestampLocal: new Date().toISOString(),
          deviceInfo: `Audio ${localStorage.getItem('preferredAudioInputId') || ''}, Video ${localStorage.getItem('preferredVideoId') || ''}, Speaker ${localStorage.getItem('preferredAudioOutputId') || ''}`,
        }).then((resp) => {
          if (resp) {
            setCurrentStep(5)
            setIsSkipDialogOpen(false);

            trackEvent('ai_interview_proceed_to_skip', {
              job_id: jobDetail?.id || "",
            })
          }
        }).catch((error) => {
          catchErrorLog(`Error sending welcome email: ${error}`);
        }).finally(() => {
          setLoadingSendWelcomeEmail(false);
        });
      }
    }).catch((err) => {
      catchErrorLog(`Error applying for job: ${err}`);
      setCurrentStep(5);
    })
  }

  const handleContinueRejection = () => {
    if (error) {
      window.location.reload();
      return;
    }

    let applicationStatus = "";
    let dropAtStage = 0;
    if (failedOnStep === 1) {
      applicationStatus = "CV Rejected";
      dropAtStage = 0;
    } else if (failedOnStep === 2) {
      applicationStatus = "Assessment Rejected";
      dropAtStage = 1;
    } else if (failedOnStep === 3) {
      applicationStatus = "AI Interview Rejected";
      dropAtStage = 2;
    }

    setLoadingContinue(true);
    applyForJob({
      job_id: jobDetail?.id || "",
      is_draft: false,
      drop_at_stage: dropAtStage,
    }).then(async (resp) => {
      if (resp) {
        await setApplicationStatus(resp.id, applicationStatus);
        setCurrentStep(5);
      }
    }).catch((err) => {
      catchErrorLog(`Error applying for job: ${err}`);
      setCurrentStep(5);
    }).finally(() => {
      setLoadingContinue(false);
    });
  }

  const onSkipInterview = () => {
    setIsSkipDialogOpen(true);
    trackEvent('ai_interview_do_it_later_clicked', {
      job_id: jobDetail?.id || "",
    });
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
          if (isLoadingScore) {
            return (
              <div className="mx-auto sm:w-[550px] mt-[4%] sm:mt-[100px]">
                <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6"
                  style={{
                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">
                    Reviewing Your CV
                  </h2>
                  <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600`} />
                </div>
              </div>
            );
          }

          if (score <= MIN_SCORE_CCBV_RESUME) {
            return (
              <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
                <ApplyRejectionCard
                  isLoading={isLoadingContinue}
                  variant="resume"
                  state={error ? "error" : "rejection"}
                  title={stageApplyInfo.resume.title}
                  message={stageApplyInfo.resume.message}
                  skills={stageApplyInfo.resume.skills}
                  recommendations={stageApplyInfo.resume.recommendations}
                  continueUrl={handleContinueRejection}
                  continueText={isLoadingContinue ? "Please wait" : error ? "Try Again" : "Continue"}
                />
              </div>
            );
          }
        }

        handleNextStep();
        return null;

      case 2:
        if (inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
          if (isLoadingScore) {
            return (
              <div className="mx-auto sm:w-[550px] mt-[4%] sm:mt-[100px]">
                <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6"
                  style={{
                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white text-center">
                    Reviewing Your Responses
                  </h2>
                  <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600`} />
                </div>
              </div>
            );
          }

          if (score <= MIN_SCORE_CCBV_ASSESSMENT) {
            return (
              <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
                <ApplyRejectionCard
                  isLoading={isLoadingContinue}
                  variant="assessment"
                  state={error ? "error" : "rejection"}
                  title={stageApplyInfo.assessment.title}
                  message={stageApplyInfo.assessment.message}
                  skills={stageApplyInfo.assessment.skills}
                  recommendations={stageApplyInfo.assessment.recommendations}
                  continueUrl={handleContinueRejection}
                  continueText={isLoadingContinue ? "Please wait" : error ? "Try Again" : "Continue"}
                />
              </div>
            );
          }

          if (!assessment) {
            handleNextStep();
            return null;
          }

          return (
            <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
              <Quiz
                surveyCompleted={surveyCompleted}
                assessment={assessment as FormAssessment}
                jobId={jobDetail?.id as string}
                onComplete={() => {
                  setSurveyCompleted(true);
                  handleCheckAssessmentScore();
                }}
                onBack={() => handlePrevStep()}
              />
            </div>
          )
        } else if (isLoadingAIInterview) {
          return (
            <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
              <div className="flex items-center justify-center h-screen">
                <p className="text-lg font-semibold">Loading AI interview...</p>
              </div>
            </div>
          )
        } else if (cvData && jobDetail && import.meta.env.VITE_AI_INTERNSHIP_JOB_ID === jobDetail?.id && interviewAssessment) {
          if (interviewCompleted) {
            handleNextStep();
            return null;
          }
          return (
            <div className="container mx-auto px-0 sm:px-0 pt-0 pb-2 sm:py-4 max-w-6xl">
              <DailyProvider callObject={callObject}>
                <SpeechDetectionProvider
                  volumeThreshold={30}
                  silenceDelay={400}
                  autoStart={true}
                >
                  <CustomVoiceInterview
                    interviewId={interviewAssessment.id || jobDetail.id}
                    pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                    agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                    authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                    s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                    s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                    awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                    fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                    userId={userInfo?.id || ''}
                    userCV={formatCV(cvData.CV_data)}
                    userName={cvData.CV_data.name || ''}
                    isSkipInterview={false}
                    isLoadingSkipInterview={false}
                    onCloseModal={() => {
                      handleNextStep();
                    }}
                    onCompletedInterview={() => {
                      console.log("Interview completed");
                    }}
                    sections={formattedSections}
                    interviewName={interviewAssessment.content.interviewName}
                    description={interviewAssessment.content.description}
                    maxDuration={interviewAssessment.content.maxDuration || 20}
                    interviewContext={interviewAssessment.content.interviewContext || "You are an interviewer for a job position."}
                    closingStatement={interviewAssessment.content.closingStatement || ""}
                    interviewerPersona={interviewAssessment?.content.interviewerPersona || ""}
                    toneAndCommunication={interviewAssessment?.content.toneAndCommunication || undefined}
                    interviewFlowPrinciples={interviewAssessment?.content.interviewFlowPrinciples || undefined}
                    informationToShare={interviewAssessment?.content.informationToShare || undefined}
                    customGreeting={interviewAssessment.content.customGreeting || ""}
                    companyPreferences={companyInfo?.preferences}
                    handleEndSession={() => {
                      setHideBackButton(true);
                    }}
                  />
                </SpeechDetectionProvider>
              </DailyProvider>
            </div>
          )
        }

        if (interviewAssessment) {
          if (interviewCompleted) {
            handleNextStep();
            return null;
          }

          if (isLoadingScore) {
            return (
              <div className="mx-auto sm:w-[550px] mt-[4%] sm:mt-[100px]">
                <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6"
                  style={{
                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">
                    Reviewing Your CV
                  </h2>
                  <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600`} />
                </div>
              </div>
            );
          }

          return (
            <div className="container mx-auto px-0 sm:px-0 pt-0 pb-2 sm:py-8 max-w-6xl">
              {jobDetail && cvData && interviewAssessment && (
                <DailyProvider callObject={callObject}>
                  <SpeechDetectionProvider
                    volumeThreshold={30}
                    silenceDelay={400}
                    autoStart={true}
                  >
                    <CustomVoiceInterview
                      interviewId={interviewAssessment.id || jobDetail.id}
                      pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                      agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                      authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                      s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                      s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                      awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                      fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                      userId={userInfo?.id || ''}
                      userCV={formatCV(cvData.CV_data)}
                      userName={cvData.CV_data.name || ''}
                      isSkipInterview={false}
                      isLoadingSkipInterview={false}
                      onCloseModal={() => {
                        handleCheckInterviewScore();
                      }}
                      onCompletedInterview={() => {
                        console.log("Interview completed");
                      }}
                      sections={formattedSections}
                      interviewName={interviewAssessment.content.interviewName}
                      description={interviewAssessment.content.description}
                      maxDuration={interviewAssessment.content.maxDuration || 20}
                      interviewContext={interviewAssessment.content.interviewContext || "You are an interviewer for a job position."}
                      closingStatement={interviewAssessment.content.closingStatement || ""}
                      interviewerPersona={interviewAssessment?.content.interviewerPersona || ""}
                      toneAndCommunication={interviewAssessment?.content.toneAndCommunication || undefined}
                      interviewFlowPrinciples={interviewAssessment?.content.interviewFlowPrinciples || undefined}
                      informationToShare={interviewAssessment?.content.informationToShare || undefined}
                      customGreeting={interviewAssessment.content.customGreeting || ""}
                      job_id={jobDetail.id}
                      interviewLanguage={interviewAssessment.content.interviewLanguage || "en-US"}
                      voice={interviewAssessment.content.voice || "male"}
                      idleTimeoutMessage={interviewAssessment.content.idleTimeoutMessage}
                      companyPreferences={companyInfo?.preferences}
                      handleEndSession={() => {
                        setHideBackButton(true);
                      }}
                    />

                  </SpeechDetectionProvider>
                </DailyProvider>
              )}
            </div>
          );
        } else {
          if (interviewCompleted) {
            handleNextStep();
            return null;
          }

          // If no custom interview assessment, show AI Interview
          return (
            <div className="container mx-auto px-0 sm:px-0 pt-0 pb-2 sm:py-8 max-w-6xl">
              <AiInterview
                isApplyFlow
                routeTo={'/dashboard'}
                interviewType="voice"
                showLayout={false}
                handleStarted={(deviceInfo: string) => {
                  trackEvent('take_ai_interview', {
                    candidate_id: userInfo?.id || "",
                    device_info: deviceInfo,
                  })
                }}
                handleEnd={() => {
                  trackEvent('ai_interview_completed', {
                    job_id: jobDetail?.id || "",
                  })
                  handleNextStep()
                }}
                onSkipInterview={onSkipInterview}
                isSkipInterview={false}
                isShowBrand={companyInfo?.is_show_brand}
                companyPreferences={companyInfo?.preferences}
              />
            </div>
          );
        }

      case 3:
        if (inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
          if (interviewAssessment) {
            if (isLoadingScore) {
              return (
                <div className="mx-auto sm:w-[550px] mt-[4%] sm:mt-[100px]">
                  <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6"
                    style={{
                      boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">
                      Reviewing Your AI Interview
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm md:text-md">
                      {tips[currentTipIndex]}
                    </p>
                    <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600`} />
                  </div>
                </div>
              );
            }

            if (score <= MIN_SCORE_CCBV_AI_INTERVIEW) {
              return (
                <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
                  <ApplyRejectionCard
                    isLoading={isLoadingContinue}
                    variant="interview"
                    state={error ? "error" : "rejection"}
                    title={stageApplyInfo.interview.title}
                    message={stageApplyInfo.interview.message}
                    skills={stageApplyInfo.interview.skills}
                    recommendations={stageApplyInfo.interview.recommendations}
                    continueUrl={handleContinueRejection}
                    continueText={isLoadingContinue ? "Please wait" : error ? "Try Again" : "Continue"}
                  />
                </div>
              );
            }

            return (
              <div className="container mx-auto px-0 sm:px-0 pt-0 pb-2 sm:py-8 max-w-6xl sm:mt-[4.5%]">
                {jobDetail && cvData && interviewAssessment && (
                  <DailyProvider callObject={callObject}>
                    <SpeechDetectionProvider
                      volumeThreshold={30}
                      silenceDelay={400}
                      autoStart={true}
                    >
                      <CustomVoiceInterview
                        interviewId={interviewAssessment.id || jobDetail.id}
                        pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                        agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                        authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                        s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                        s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                        awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                        fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                        userId={userInfo?.id || ''}
                        userCV={formatCV(cvData.CV_data)}
                        userName={cvData.CV_data.name || ''}
                        isSkipInterview={false}
                        isLoadingSkipInterview={false}
                        onCloseModal={() => {
                          handleCheckInterviewScore();
                        }}
                        onCompletedInterview={() => {
                          console.log("Interview completed");
                        }}
                        sections={formattedSections}
                        interviewName={interviewAssessment.content.interviewName}
                        description={interviewAssessment.content.description}
                        maxDuration={interviewAssessment.content.maxDuration || 20}
                        interviewContext={interviewAssessment.content.interviewContext || "You are an interviewer for a job position."}
                        closingStatement={interviewAssessment.content.closingStatement || ""}
                        interviewerPersona={interviewAssessment?.content.interviewerPersona || ""}
                        toneAndCommunication={interviewAssessment?.content.toneAndCommunication || undefined}
                        interviewFlowPrinciples={interviewAssessment?.content.interviewFlowPrinciples || undefined}
                        informationToShare={interviewAssessment?.content.informationToShare || undefined}
                        customGreeting={interviewAssessment.content.customGreeting || ""}
                        job_id={jobDetail.id}
                        interviewLanguage={interviewAssessment.content.interviewLanguage || "en-US"}
                        voice={interviewAssessment.content.voice || "male"}
                        companyPreferences={companyInfo?.preferences}
                        handleEndSession={() => {
                          setHideBackButton(true);
                        }}
                      />

                    </SpeechDetectionProvider>
                  </DailyProvider>
                )}
              </div>
            );
          }

          if (isLoadingScore) {
            return (
              <div className="mx-auto sm:w-[550px] mt-[4%] sm:mt-[100px]">
                <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6"
                  style={{
                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">
                    Reviewing Your AI Interview
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm md:text-md">
                    {tips[currentTipIndex]}
                  </p>
                  <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600`} />
                </div>
              </div>
            );
          }

          if (score <= MIN_SCORE_CCBV_AI_INTERVIEW) {
            return (
              <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
                <ApplyRejectionCard
                  isLoading={isLoadingContinue}
                  variant="interview"
                  state={error ? "error" : "rejection"}
                  title={stageApplyInfo.interview.title}
                  message={stageApplyInfo.interview.message}
                  skills={stageApplyInfo.interview.skills}
                  recommendations={stageApplyInfo.interview.recommendations}
                  continueUrl={handleContinueRejection}
                  continueText={isLoadingContinue ? "Please wait" : error ? "Try Again" : "Continue"}
                />
              </div>
            );
          }

          if (interviewCompleted) {
            handleNextStep();
            return null;
          }

          // If no custom interview assessment, show AI Interview
          return (
            <div className="container mx-auto px-0 sm:px-0 pt-0 pb-2 sm:py-4 max-w-6xl">
              <AiInterview
                isApplyFlow
                routeTo={'/dashboard'}
                interviewType="voice"
                companyPreferences={companyInfo?.preferences}
                showLayout={false}
                handleStarted={(deviceInfo: string) => {
                  trackEvent('take_ai_interview', {
                    candidate_id: userInfo?.id || "",
                    device_info: deviceInfo,
                  })
                }}
                handleEnd={() => {
                  handleCheckInterviewScore();
                }}
                isSkipInterview={false}
                onSkipInterview={onSkipInterview}
                handleEndSession={() => {
                  setHideBackButton(true);
                }}
              />
            </div>
          );
        }

        if (!assessment) {
          handleNextStep();
          return null;
        }

        return (
          <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl sm:mb-[4.5%]">
            <Quiz
              surveyCompleted={surveyCompleted}
              assessment={assessment as FormAssessment}
              jobId={jobDetail?.id as string}
              onComplete={() => {
                setSurveyCompleted(true);
                handleNextStep();
              }}
              onBack={() => handlePrevStep()}
            />
          </div>
        )

      case 4:
        return (
          <div className="container mx-auto px-0 sm:px-4 pt-0 pb-20 sm:py-8 max-w-4xl sm:pb-40">
            <WorkAuthorization
              job={jobDetail || undefined}
              onComplete={(value: JobApplication) => {
                setJobApplication(value);
                handleNextStep();
                sessionStorage.removeItem("__referral_code__");
              }}
            />
          </div>
        )

      case 5:
        if (inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") && (
          score <= MIN_SCORE_CCBV_RESUME ||
          score <= MIN_SCORE_CCBV_ASSESSMENT ||
          score <= MIN_SCORE_CCBV_AI_INTERVIEW
        )) {
          return (
            <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
              <ApplicationRejection company={companyInfo} />
            </div>
          )
        }

        if (isSkippedAIInterview) {
          return (
            <div className={cn("container mx-auto max-w-4xl  flex sm:items-center")}>
              <ApplicationDraft companyPreferences={companyInfo?.preferences} />
            </div>
          )
        }

        return (
          <div className="container mx-auto px-0 sm:px-4 pt-0 sm:py-8 max-w-4xl pb-20 sm:pb-36">
            <Complete
              job_application={jobApplication}
            />
          </div>
        );

      default:
        return (
          <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-4xl">
            <ResumeUpload />
          </div>
        );
    }
  };

  const progressSteps: ProgressStep[] = [
    {
      label: inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") ? "Continue" : "Resume Upload",
      isActive: currentStep === 1,
      isCompleted: currentStep > 1,
    },
    {
      label: inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") ? "Screening" : "AI Interview",
      isActive: currentStep === 2,
      isCompleted: currentStep > 2,
      isHide: inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") ? !assessment : false,
    },
    {
      label: inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") ? "AI Interview" : "Screening",
      isActive: currentStep === 3,
      isCompleted: currentStep > 3,
      isHide: !inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") ? !assessment : false,
    },
    {
      label: "Work Authorization Declaration",
      isActive: currentStep === 4,
      isCompleted: currentStep > 4,
      isHide: jobDetail?.is_enable_work_authorization ? false : true,
    },
    {
      label: "Complete",
      isActive: currentStep === 5,
      isCompleted: currentStep > 5,
    },
  ];

  let newCurrentStep = currentStep;
  if (!assessment && currentStep === 4) {
    newCurrentStep = currentStep - 1;
  }

  if (isLoadingJobDetail || isLoadingUserProfile || isLoadingCV || isLoadingCompanyInfo) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-lg font-semibold">Loading job details...</p>
      </div>
    );
  }

  return (
    <SnackbarProvider>
      <div className="container mx-auto px-0 sm:px-4 pt-0 sm:py-4 h-screen mb-10">
        <div className="container flex flex-row items-start justify-between max-w-6xl mx-auto p-2 sm:pt-5 sm:p-0 mb-5">
          <div className="flex items-center flex-1">
            {!hideBackButton && (
              <button
                onClick={() => {
                  setIsSkipDialogOpen(true)
                  trackEvent('ai_interview_waiting_room_back');
                }}
                className=" hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-md p-2 -m-2 flex gap-1 items-center"
              >
                <ChevronLeftIcon className="h-5 w-5" style={{ color: "#09090b" }} />
                Back
              </button>
            )}
          </div>
          <div>
            {!hideBackButton && companyInfo?.preferences && (
              <img src={companyInfo.is_show_brand ? `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${companyInfo.preferences.company_logo}` : "https://assets.polymet.ai/eligible-rose-215732"} alt={companyInfo.company_name} className="mx-auto h-10 sm:h-20" />
            )}
          </div>
          <div className="flex-1"></div>
        </div>
        {renderCurrentStep()}
      </div>
      <div className="w-full fixed bottom-0 left-0 right-0 px-4 pt-6 sm:pt-4 pb-6 sm:pb-4 bg-white shadow-sm">
        <ProgressBar currentStep={newCurrentStep} isShowBanding={companyInfo?.is_show_brand} companyPreferences={companyInfo?.preferences} steps={progressSteps} />
      </div>

      <SkipInterviewDialog
        isOpen={isSkipDialogOpen}
        isLoading={isLoadingSendWelcomeEmail}
        onClose={() => {
          setIsSkipDialogOpen(false)
        }}
        onReturnToInterview={() => {
          setIsSkipDialogOpen(false)
          trackEvent('ai_interview_return_to_interview_clicked', {
            job_id: jobDetail?.id || "",
          });
        }}
        onProceedToSkip={handleSkipInterivew}
      />
    </SnackbarProvider>
  );
}
